import { isState } from "@snap/state-management";
import { Injectable } from "@snap/ts-inject";
import type { MetricsClient } from "../clients/metricsClient";
import { metricsClientFactory } from "../clients/metricsClient";
import type { LensKeyboard } from "../session/LensKeyboard";
import { lensKeyboardFactory } from "../session/LensKeyboard";
import type { LensState } from "../session/lensState";
import { lensStateFactory } from "../session/lensState";
import type { LensRepository } from "../lens/LensRepository";
import { lensRepositoryFactory } from "../lens/LensRepository";
import type { SessionState } from "../session/sessionState";
import { sessionStateFactory } from "../session/sessionState";
import { getLogger } from "../logger/logger";
import { lensCoreFactory } from "../lens-core-module/loader/lensCoreFactory";
import type { LensCore } from "../lens-core-module/lensCore";
import type { CameraKitConfiguration } from "../configuration";
import { configurationToken } from "../configuration";
import type { RemoteApiSpecsClient } from "../clients/remoteApiSpecsClient";
import { remoteApiSpecsClientFactory } from "../clients/remoteApiSpecsClient";
import { createHttpUriHandler } from "./internal-handlers/httpUriHandler";
import type { UriHandlers, UriResponse } from "./UriHandlers";
import { extractSchemeAndRoute, isUriHandlers, isUriResponse, uriHandlersFactory } from "./UriHandlers";
import type { RemoteApiServices } from "./internal-handlers/remoteApiUriHandler";
import { createRemoteApiUriHandler, remoteApiServicesFactory } from "./internal-handlers/remoteApiUriHandler";

const logger = getLogger("uriHandlersRegister");

/**
 * Registers URI handlers within LensCore.
 * @internal
 */
export const registerUriHandlers = Injectable(
    "registerUriHandlers",
    [
        configurationToken,
        lensCoreFactory.token,
        lensStateFactory.token,
        uriHandlersFactory.token,
        lensKeyboardFactory.token,
        remoteApiServicesFactory.token,
        lensRepositoryFactory.token,
        sessionStateFactory.token,
        metricsClientFactory.token,
        remoteApiSpecsClientFactory.token,
    ] as const,
    (
        configuration: CameraKitConfiguration,
        lensCore: LensCore,
        lensState: LensState,
        userHandlers: UriHandlers,
        lensKeyboard: LensKeyboard,
        remoteApiServices: RemoteApiServices,
        lensRepository: LensRepository,
        sessionState: SessionState,
        metrics: MetricsClient,
        remoteApiSpecsClient: RemoteApiSpecsClient
    ): void => {
        if (!isUriHandlers(userHandlers)) {
            throw new Error("Expected an array of UriHandler objects");
        }

        // Users can provide custom UriHandlers using the `uriHandlersFactory.token`.
        // However, we need to include some internally-defined handlers (e.g., Lens Keyboard and Remote API)
        // before registering the handlers with LensCore.
        // Note: The internal HTTP handler can be overridden if the user prefers to disable validation, for instance.
        const allHandlers = [
            createHttpUriHandler(lensState, sessionState, remoteApiSpecsClient, configuration.lensHttpHandler),
            ...userHandlers,
            lensKeyboard.uriHandler,
            createRemoteApiUriHandler(remoteApiServices, sessionState, lensState, lensRepository, metrics),
        ];

        for (const { uri, handleRequest, cancelRequest } of allHandlers) {
            const uris = Array.isArray(uri) ? uri : [uri];
            for (const { scheme, route } of uris.map(extractSchemeAndRoute)) {
                lensCore.registerUriListener(scheme, route, {
                    handleRequest: (request) => {
                        const reply = (response: UriResponse) => {
                            if (!isUriResponse(response)) {
                                throw new Error("Expected UriResponse object");
                            }
                            lensCore.provideUriResponse(request.identifier, response);
                        };

                        // Since lenses are the only things that make URI requests, we expect to always be in the
                        // "lensApplied" state – we'll sanity check, though, and log a warning if we're not.
                        const state = lensState.getState();
                        if (isState(state, "noLensApplied")) {
                            logger.warn(
                                `Got a URI request for ${request.uri}, but there is no active lens. The ` +
                                    `request will not be processed.`
                            );
                            return;
                        }

                        // NOTE: we do not handle any error thrown on an extension side when handleRequest() is called.
                        // That responsibility is delegated to the extension by design and that is exactly what Android
                        // and iOS SDKs do.
                        handleRequest(request, reply, state.data);
                    },
                    cancelRequest: (request) => {
                        if (cancelRequest) {
                            const state = lensState.getState();
                            if (isState(state, "noLensApplied")) {
                                logger.warn(
                                    `Got a URI cancel request for ${request.uri}, but there is no active ` +
                                        `lens. The cancel request will not be processed.`
                                );
                                return;
                            }
                            cancelRequest(request, state.data);
                        }
                    },
                });
            }
        }
    }
);
